
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { normalizeDescription } from "@/utils/contentUtils";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";

export const useContentOperations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const refreshData = () => {
    console.log("[ContentOperations] Refreshing data caches");
    queryClient.invalidateQueries({ queryKey: ['contentItems'] });
    queryClient.invalidateQueries({ queryKey: ['collections'] });
    queryClient.invalidateQueries({ queryKey: ['contentItem'] });
  };

  const handleContentClick = (id: string) => {
    console.log(`[ContentOperations] handleContentClick called with id: ${id}`);
    
    // Track navigation to content - using generic event tracking instead of trackNavigation
    Tier1Tracking.trackEvent('content_viewed', 'discovery', { content_id: id }, user);
    
    console.log(`[ContentOperations] Navigating to /content/${id}`);
    navigate(`/content/${id}`);
  };

  const handleUpdateContent = async (id: string, updates: { title?: string; description?: string }) => {
    if (!id) {
      console.error("[ContentOperations] Cannot update content: Missing content ID");
      toast.error("Cannot update: Missing content ID");
      return false;
    }

    try {
      // Normalize the description if it's being updated
      const normalizedUpdates = updates.description !== undefined
        ? { ...updates, description: normalizeDescription(updates.description) }
        : updates;
        
      console.log(`[ContentOperations] Updating content ${id} with:`, {
        updates,
        normalized: normalizedUpdates
      });
        
      setIsLoading(true);
      
      // Direct database update without any user_id check
      const { error } = await supabase
        .from("content_items")
        .update(normalizedUpdates)
        .eq("id", id);

      if (error) {
        console.error("[ContentOperations] Error updating content:", error);
        throw error;
      }

      // Track content editing
      const fieldsChanged: string[] = [];
      if (updates.title !== undefined) fieldsChanged.push('title');
      if (updates.description !== undefined) fieldsChanged.push('description');
      
      if (fieldsChanged.length > 0) {
        Tier1Tracking.trackContentEdited(id, fieldsChanged, user);
      }

      console.log(`[ContentOperations] Content updated successfully:`, normalizedUpdates);
      refreshData();
      return true;
    } catch (error: any) {
      console.error("Error updating content:", error);
      toast.error(error.message || "Failed to update content");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!id) {
      toast.error("Cannot delete: Missing content ID");
      return;
    }

    try {
      setIsLoading(true);
      
      // Remove any collection associations first
      const { error: collectionError } = await supabase
        .from("content_collections")
        .delete()
        .eq("content_id", id);

      if (collectionError && collectionError.code !== 'PGRST116') { // Ignore if no rows deleted
        console.error("Error removing collection associations:", collectionError);
      }
      
      // Then delete the content item
      const { error } = await supabase
        .from("content_items")
        .delete()
        .eq("id", id);

      if (error) {
        throw error;
      }

      // Track content deletion
      Tier1Tracking.trackEvent('content_deleted', 'content_management', { content_id: id }, user);

      toast.success("Content deleted successfully!");
      refreshData();
    } catch (error: any) {
      console.error("Error deleting content:", error);
      toast.error(error.message || "Failed to delete content");
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchive = async (id: string) => {
    if (!id) {
      toast.error("Cannot archive: Missing content ID");
      return;
    }

    try {
      setIsLoading(true);
      
      const { data, error: checkError } = await supabase
        .from("content_items")
        .select("archived")
        .eq("id", id)
        .single();
        
      if (checkError) {
        throw checkError;
      }
      
      const newArchivedStatus = !(data?.archived);
      
      // Update archive status without any user_id check
      const { error } = await supabase
        .from("content_items")
        .update({ archived: newArchivedStatus })
        .eq("id", id);
      
      if (error) {
        throw error;
      }
      
      // Track archiving action
      Tier1Tracking.trackEvent(
        newArchivedStatus ? 'content_archived' : 'content_unarchived',
        'content_management',
        { content_id: id, archived: newArchivedStatus },
        user
      );
      
      toast.success(newArchivedStatus ? "Content archived successfully!" : "Content unarchived successfully!");
      
      // Add hard page refresh after successful archive/unarchive
      console.log("[ARCHIVE] Adding hard page refresh after archive operation");
      setTimeout(() => {
        console.log("[ARCHIVE] Executing window.location.reload()");
        window.location.reload();
      }, 300);
      
    } catch (error: any) {
      console.error("Error updating archive status:", error);
      toast.error(error.message || "Failed to update archive status");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleContentClick,
    handleDelete,
    handleArchive,
    handleUpdateContent,
    isLoading
  };
};

export default useContentOperations;
